import { useState, useMemo, useEffect } from 'react';
import Modal from '@components/widgets/Modal';
import Button from '@components/forms/Button';
import { ReactComponent as SearchIcon } from '@assets/icons/search_icon.svg';
import { ReactComponent as CheckIcon } from '@assets/icons/check_icon.svg';
import clsx from 'clsx';

interface LenderOption {
  organisationId: string;
  name: string;
}

interface Props {
  open: boolean;
  prospects: LenderOption[];
  initialSelectedId?: string;
  onClose: () => void;
  onSubmit: (organisationIds: string[]) => Promise<void>;
}

const ProspectLenderModal = ({ open, prospects, initialSelectedId, onClose, onSubmit }: Props) => {
  const [search, setSearch] = useState('');
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      setSelectedIds(initialSelectedId ? new Set([initialSelectedId]) : new Set());
      setSearch('');
    }
  }, [open, initialSelectedId]);

  const filtered = useMemo(() =>
    prospects.filter((p) =>
      !search || p.name.toLowerCase().includes(search.toLowerCase())
    ),
    [prospects, search]
  );

  const toggle = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const handleClose = () => {
    setSearch('');
    setSelectedIds(initialSelectedId ? new Set([initialSelectedId]) : new Set());
    onClose();
  };

  const handleSubmit = async () => {
    if (selectedIds.size === 0) return;
    setIsSubmitting(true);
    try {
      await onSubmit(Array.from(selectedIds));
      handleClose();
    } finally {
      setIsSubmitting(false);
    }
  };

  const selectedList = prospects.filter((p) => selectedIds.has(p.organisationId));
  const count = selectedIds.size;

  return (
    <Modal title="Register your interest" size="md" open={open} onClose={handleClose}>
      <div className="flex flex-col gap-3">
        <p className="text-sm text-grey-600">
          Select one or more lenders you'd like to use Authentik8 with. We'll let you know as soon as they come onboard.
        </p>

        {selectedList.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {selectedList.map((p) => (
              <span
                key={p.organisationId}
                className="inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full cursor-pointer bg-secondary/[0.08] text-secondary border border-secondary/25"
                onClick={() => toggle(p.organisationId)}>
                {p.name}
                <span className="text-sm leading-none text-secondary-light">×</span>
              </span>
            ))}
          </div>
        )}

        <div className="relative">
          <SearchIcon className="absolute top-1/2 left-3 -translate-y-1/2 text-grey-400 z-10 w-4 h-4" />
          <input
            className="w-full border border-grey-300 rounded-lg pl-9 pr-3 py-2 text-sm text-grey-900 focus:outline-none focus:border-secondary"
            placeholder="Search lenders..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        <div className="border border-grey-200 rounded-lg overflow-hidden max-h-56 overflow-y-auto">
          {filtered.map((p) => {
            const isSelected = selectedIds.has(p.organisationId);
            return (
              <div
                key={p.organisationId}
                className={clsx(
                  'flex items-center gap-3 px-3 py-2.5 border-b border-grey-100 last:border-b-0 cursor-pointer text-sm transition-colors',
                  isSelected ? 'bg-blue-50 font-semibold text-secondary' : 'text-grey-700 hover:bg-grey-50'
                )}
                onClick={() => toggle(p.organisationId)}>
                <div className={clsx(
                  'w-4 h-4 rounded flex items-center justify-center flex-shrink-0 border',
                  isSelected ? 'border-secondary bg-secondary' : 'bg-white border-grey-300'
                )}>
                  {isSelected && <CheckIcon className="w-2.5 h-2.5 text-white" />}
                </div>
                {p.name}
              </div>
            );
          })}
          {filtered.length === 0 && (
            <p className="text-sm text-grey-400 text-center py-6">No lenders match your search.</p>
          )}
        </div>

        <div className="flex items-center justify-between pt-1">
          <span className="text-sm text-grey-500">
            {count > 0 && (
              <><span className="font-semibold text-secondary">{count}</span> selected</>
            )}
          </span>
          <div className="flex gap-2">
            <Button
              variant="outlined"
              className="border border-grey-300 text-grey-700 normal-case font-semibold rounded-lg text-sm py-2 px-4 bg-white"
              onClick={handleClose}>
              Cancel
            </Button>
            <Button
              className="bg-secondary text-white normal-case font-semibold rounded-lg text-sm py-2 px-4 disabled:opacity-50"
              disabled={count === 0 || isSubmitting}
              onClick={handleSubmit}>
              {isSubmitting ? 'Registering...' : `Register interest${count > 0 ? ` (${count})` : ''}`}
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default ProspectLenderModal;
